import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["getColumnDefs", "defaultDateFormat", "errors", "rowData", "onRef", "shouldRefreshColumnDefs"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useRef, useEffect, useState, useCallback, useMemo } from 'react';
import SectionList from "../SectionList";
import { useWeb5Context } from "../../lib/Web5Context";
import SwitchCell from "./editCells/SwitchCell";
import DatePickerCell from "./editCells/DatePickerCell";
import SelectCell from "./editCells/SelectCell";
import InputCell from "./editCells/InputCell";
import CarouselCell from "./editCells/CarouselCell";
import { createUseStyles } from '@Sage/hoi-poi-ui';
import styles from "./styles";
const useStyles = createUseStyles(styles, 'SectionList');
const getInputPlaceholder = crudType => {
  switch (crudType) {
    case 'natural':
    case 'number':
    case 'integer':
      return 'placeholder_numeric';
    case 'currency':
    case 'decimal':
      return 'placeholder_amount';
    case 'percentage':
      return 'placeholder_percentage';
    case 'singleValueList':
      return 'label_selectone';
    case 'fuzzySearchSingle':
      return 'placeholder_search';
    case 'text':
    case 'textarea':
      return 'placeholder_text_field';
    case 'date':
      return 'placeholder_date_format';
    default:
      return '';
  }
};
const SectionListCrud = /*#__PURE__*/memo(_ref => {
  let {
      getColumnDefs,
      defaultDateFormat,
      errors,
      rowData,
      onRef,
      shouldRefreshColumnDefs
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const classes = useStyles();
  const errorsRef = useRef(errors);
  const tableRef = useRef(null);
  const isFirstLoad = useRef(true);
  const [columnDefs, setColumnsDefs] = useState([]);
  const {
    t
  } = useWeb5Context();
  const [hasErrors, setHasErrors] = useState(false);
  const crudComponents = useMemo(() => {
    return {
      SwitchCell,
      DatePickerCell,
      SelectCell,
      InputCell,
      CarouselCell
    };
  }, []);
  const transformTableToCrud = useCallback(_ref2 => {
    let {
      columnDefs,
      crudSchema,
      inputProps,
      formatDate,
      getCurrencySymbolPosition
    } = _ref2;
    const mappedCrudSchema = crudSchema.reduce((obj, current) => {
      obj[current.id] = current;
      return obj;
    }, {});
    const editCells = {
      text: 'InputCell',
      textarea: 'InputCell',
      integer: 'InputCell',
      decimal: 'InputCell',
      number: 'InputCell',
      percentage: 'InputCell',
      currency: 'InputCell',
      bool: 'SwitchCell',
      singleValue: 'InputCell',
      dateTime: 'DatePickerCell',
      date: 'DatePickerCell',
      singleValueList: 'SelectCell',
      fuzzySearchSingle: 'SelectCell',
      carousel: 'CarouselCell'
    };
    const crudColumnDef = columnDefs.map(current => {
      var _errorsRef$current;
      const crudField = mappedCrudSchema[current.colId] || null;
      if (!crudField) return current;
      const crudCellRenderer = editCells[current.crudType] || editCells[crudField.type] || '';
      if (!crudCellRenderer) return current;

      // IMPORTANT
      // >> crudField.type is related to extraFieldsSchema for extraFields
      // >> current.crudType is related to the colDef of the standardFields
      // so when is extrafield we look in crudField.type and when is standardField we look in current.crudType

      const placeholder = getInputPlaceholder(crudField.type || current.crudType);
      current.cellRenderer = crudCellRenderer;
      current.cellRendererParams = _objectSpread(_objectSpread({}, current.cellRendererParams), {}, {
        inputParams: {
          onChange: inputProps.onChange,
          isMandatory: crudField.mandatory || false,
          isReadOnly: crudField.readOnly || false,
          dataLength: crudField.dataLength,
          defaultValue: crudField.defaultValue,
          placeholder: t(placeholder),
          errors: (errorsRef === null || errorsRef === void 0 ? void 0 : (_errorsRef$current = errorsRef.current) === null || _errorsRef$current === void 0 ? void 0 : _errorsRef$current[current.colId]) || null
        }
      });
      if (crudField.mandatory) {
        current.headerComponent = 'headerTextCell';
        current.headerComponentParams = _objectSpread(_objectSpread({}, current.headerComponentParams), {}, {
          isMandatory: true
        });
      }
      if (crudField.type === 'natural' || crudField.type === 'integer' || crudField.type === 'number' || crudField.type === 'decimal' || crudField.type === 'currency' || crudField.type === 'percentage' || current.crudType === 'natural' || current.crudType === 'integer' || current.crudType === 'number' || current.crudType === 'decimal' || current.crudType === 'currency' || current.crudType === 'percentage') {
        current.cellRendererParams.inputParams.type = crudField.type || current.crudType;
      }
      if ((crudField.type === 'currency' || current.crudType === 'currency') && getCurrencySymbolPosition) {
        const currencyPosition = getCurrencySymbolPosition();
        current.cellRendererParams.currencyPosition = currencyPosition;
      }
      if ((crudField.type === 'date' || current.crudType === 'date') && formatDate) {
        current.cellRendererParams.inputParams.formatDate = formatDate;
      }
      if (crudField.type === 'singleValueList' || current.crudType === 'singleValueList') {
        var _crudField$inputAttrs, _crudField$inputAttrs2;
        current.cellRendererParams.inputParams = _objectSpread(_objectSpread({}, current.cellRendererParams.inputParams), {}, {
          list: crudField === null || crudField === void 0 ? void 0 : (_crudField$inputAttrs = crudField.inputAttrs) === null || _crudField$inputAttrs === void 0 ? void 0 : _crudField$inputAttrs.list,
          options: [],
          isClearable: true
        });
        if (crudField !== null && crudField !== void 0 && (_crudField$inputAttrs2 = crudField.inputAttrs) !== null && _crudField$inputAttrs2 !== void 0 && _crudField$inputAttrs2.list && inputProps.loadOptions) {
          current.cellRendererParams.inputParams = _objectSpread(_objectSpread({}, current.cellRendererParams.inputParams), {}, {
            loadOptions: inputProps.loadOptions(crudField.inputAttrs)
          });
        }
      }
      if (crudField.type === 'fuzzySearchSingle' || current.crudType === 'fuzzySearchSingle') {
        var _crudField$inputAttrs3;
        if (crudField !== null && crudField !== void 0 && (_crudField$inputAttrs3 = crudField.inputAttrs) !== null && _crudField$inputAttrs3 !== void 0 && _crudField$inputAttrs3.list && inputProps.loadOptions) {
          current.cellRendererParams.inputParams = _objectSpread(_objectSpread({}, current.cellRendererParams.inputParams), {}, {
            loadOptions: inputProps.loadOptionsFuzzy(crudField.inputAttrs),
            isFuzzy: true,
            components: inputProps.selectComponents(crudField.inputAttrs)
          });
        }
      }
      return current;
    });
    return crudColumnDef;
  }, [t]);
  const updateErrors = useCallback(() => {
    if (!errorsRef.current) return;
    rowData.forEach((current, index) => {
      Object.keys(current).forEach(key => {
        if (current[key] || typeof current[key] === 'number') {
          var _errorsRef$current2, _errorsRef$current2$k, _errorsRef$current2$k2, _errorsRef$current4, _errorsRef$current4$k, _errorsRef$current4$k2;
          if (errorsRef !== null && errorsRef !== void 0 && (_errorsRef$current2 = errorsRef.current) !== null && _errorsRef$current2 !== void 0 && (_errorsRef$current2$k = _errorsRef$current2[key]) !== null && _errorsRef$current2$k !== void 0 && (_errorsRef$current2$k2 = _errorsRef$current2$k.rows) !== null && _errorsRef$current2$k2 !== void 0 && _errorsRef$current2$k2[index]) {
            var _errorsRef$current3, _errorsRef$current3$k, _errorsRef$current3$k2;
            errorsRef === null || errorsRef === void 0 ? true : (_errorsRef$current3 = errorsRef.current) === null || _errorsRef$current3 === void 0 ? true : (_errorsRef$current3$k = _errorsRef$current3[key]) === null || _errorsRef$current3$k === void 0 ? true : (_errorsRef$current3$k2 = _errorsRef$current3$k.rows) === null || _errorsRef$current3$k2 === void 0 ? true : delete _errorsRef$current3$k2[index];
          } else if (errorsRef !== null && errorsRef !== void 0 && (_errorsRef$current4 = errorsRef.current) !== null && _errorsRef$current4 !== void 0 && (_errorsRef$current4$k = _errorsRef$current4[key.toLowerCase()]) !== null && _errorsRef$current4$k !== void 0 && (_errorsRef$current4$k2 = _errorsRef$current4$k.rows) !== null && _errorsRef$current4$k2 !== void 0 && _errorsRef$current4$k2[index]) {
            var _errorsRef$current5, _errorsRef$current5$k, _errorsRef$current5$k2;
            errorsRef === null || errorsRef === void 0 ? true : (_errorsRef$current5 = errorsRef.current) === null || _errorsRef$current5 === void 0 ? true : (_errorsRef$current5$k = _errorsRef$current5[key.toLowerCase()]) === null || _errorsRef$current5$k === void 0 ? true : (_errorsRef$current5$k2 = _errorsRef$current5$k.rows) === null || _errorsRef$current5$k2 === void 0 ? true : delete _errorsRef$current5$k2[index];
          }
        }
      });
    });
  }, [rowData]);
  useEffect(() => {
    if (!(isFirstLoad !== null && isFirstLoad !== void 0 && isFirstLoad.current) && errorsRef.current === errors && !errors && !shouldRefreshColumnDefs) return;
    if (!getColumnDefs) return;
    errorsRef.current = errors;
    updateErrors();
    getColumnDefs({
      transformTableToCrud
    }).then(columnDefs => {
      setColumnsDefs(columnDefs);
      if (tableRef.current && errorsRef.current) {
        const colIds = Object.keys(errorsRef.current).map(key => key);
        tableRef.current.refreshColumnByColId(colIds);
      }
    });
    setHasErrors(errorsRef.current && Object.keys(errorsRef.current).length > 0);
    isFirstLoad.current = false;
  }, [getColumnDefs, transformTableToCrud, errors, rowData, updateErrors, shouldRefreshColumnDefs]);
  const handleOnRef = useCallback(ref => {
    tableRef.current = ref;
    onRef(ref);
  }, [onRef]);
  const errorWrapperClass = useMemo(() => {
    let className = '';
    if (hasErrors) className = classes.errorsTable;
    return className;
  }, [classes, hasErrors]);
  return /*#__PURE__*/React.createElement("div", {
    className: "w5-section-list-crud"
  }, /*#__PURE__*/React.createElement("div", {
    className: errorWrapperClass
  }, /*#__PURE__*/React.createElement(SectionList, _extends({}, props, {
    columnDefs: columnDefs,
    components: crudComponents,
    rows: rowData,
    onRef: handleOnRef,
    rowHeight: 65,
    suppressColumnVirtualisation: true,
    suppressRowVirtualisation: true,
    rowBuffer: 20,
    forceRowUpdating: true
  }))));
});
export default SectionListCrud;